// Card de deal no kanban (Story 11.5 / AC3).
// Sortable via @dnd-kit/sortable — keyboard + touch + ARIA herdados da lib.
// DESIGN_SYSTEM: hairline border-line + shadow-md leve (proibição 03), cantos r3,
// densidade cozy, sem emoji (proibição 07), sem gradiente/sombra colorida.
// Badge de temperatura = 1 cor de estado contextual por card (proibição 08).
//
// O payload kanban é "flat" (só IDs — sem relações). Resolvemos o nome do
// responsável via mapa `usersById` (já carregado pra filtros). Lead/temperatura
// não vêm na view kanban do backend (ver AUTO-DECISION na story) — exibimos o
// que existe sem inventar dado.

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Clock, User } from 'lucide-react';

import { cn } from '@control-center/ui';

import { daysInStage, formatDealMoney } from '../../../lib/deal-helpers';
import type { CrmDealEntity } from '../../../lib/crm-deals-api';

/** Temperatura herdada do lead (AC3) — 1 cor de estado contextual por card. */
export type DealTemperature = 'frio' | 'morno' | 'quente';

export interface DealCardProps {
  deal: CrmDealEntity;
  /** Nome do responsável resolvido (mapa id→nome). Ausente = sem responsável. */
  responsibleName?: string;
  /** Temperatura herdada do lead, se disponível. Ausente = sem badge. */
  temperature?: DealTemperature;
  /** Drag desabilitado (RBAC read-only — AC8) ou move pendente. */
  disabled?: boolean;
  /** Card "em movimento" — opacidade reduzida enquanto PUT pendente (AC9). */
  isMoving?: boolean;
  /** Marca o card como "parado há +X dias" (AC7 — borda de atenção). */
  isStalled?: boolean;
  /** Abrir detalhe do deal (AC3). */
  onOpen: (deal: CrmDealEntity) => void;
}

/** Iniciais pra avatar do responsável (sem ícone-em-container — proibição 06). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '—';
}

export function DealCard({
  deal,
  responsibleName,
  temperature,
  disabled,
  isMoving,
  isStalled,
  onOpen,
}: DealCardProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: deal.id,
    data: { stage: deal.stage, deal },
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const days = daysInStage(deal.stage_entered_at);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`deal-card-${deal.id}`}
      className={cn(
        'group flex flex-col gap-2 rounded-[10px] border bg-panel p-3 text-left shadow-md',
        'transition-colors',
        disabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        isStalled ? 'border-warn/50' : 'border-line hover:border-line-2',
        (isMoving || isDragging) && 'opacity-50',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          // Evita que o clique inicie drag — abre o detalhe (AC3).
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={() => onOpen(deal)}
          className="line-clamp-2 text-left text-[13px] font-medium leading-snug text-ink underline-offset-2 transition-colors hover:underline"
        >
          {deal.title}
        </button>
        {temperature && <TemperatureBadge temperature={temperature} />}
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-2">
        <span className="num font-medium text-ink-2">{formatDealMoney(deal.value)}</span>
        {deal.status === 'open' && (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Clock size={12} strokeWidth={2} aria-hidden />
            {days} d
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-[11px] text-muted-2">
        {responsibleName ? (
          <>
            <span
              aria-hidden
              className="flex h-5 w-5 items-center justify-center rounded-full border border-line bg-panel-2 text-[9px] font-semibold text-muted"
            >
              {initials(responsibleName)}
            </span>
            <span className="truncate">{responsibleName}</span>
          </>
        ) : (
          <span className="inline-flex items-center gap-1 text-muted-3">
            <User size={12} strokeWidth={2} aria-hidden />
            Sem responsável
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Badge de temperatura — herdada do lead (AC3). 1 cor de estado contextual por
// card (proibição 08): quente=warn, morno=ink-2 neutro, frio=muted. Badge minimal
// dot+texto sem fundo sólido (DESIGN_SYSTEM §Badges). Só renderiza quando há dado
// (proibição 12: nada de stat fake) — o badge é omitido se temperature ausente.
// =============================================================================

const TEMPERATURE_META: Record<DealTemperature, { label: string; dot: string; text: string }> = {
  quente: { label: 'Quente', dot: 'bg-warn', text: 'text-warn' },
  morno: { label: 'Morno', dot: 'bg-ink-2', text: 'text-ink-2' },
  frio: { label: 'Frio', dot: 'bg-muted-3', text: 'text-muted-2' },
};

function TemperatureBadge({ temperature }: { temperature: DealTemperature }): JSX.Element {
  const meta = TEMPERATURE_META[temperature];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-[6px] border border-line px-1.5 py-[1px] text-[10px] font-medium',
        meta.text,
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
}
